import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useDispatch } from "react-redux";
import AddButton, { DraftButton } from "../../../../components/buttons/button";
import CloseIcon from "../../../../assets/icons/close-icon";
import InputField from "../../../../components/forms/input-field";
import CheckBox from "../../../../components/forms/check-box-field";
import successToast from "../../../../components/helper-components/success-toast";
import errorToast from "../../../../components/helper-components/error-toast";
import { warehouseBinUpdateAction } from "../../../../redux/actions/inventory-action";

const validationSchema = Yup.object().shape({
  bin_location_no: Yup.string().required("Bin location no. is required"),
});

const checkboxItems = [
  { label: "Sellable", name: "is_sellable_bin_location", isRequired: false },
  {
    label: "Non-Sellable",
    name: "is_non_sellable_bin_location",
    isRequired: false,
  },
  { label: "Expired", name: "is_expired_bin_location", isRequired: false },
];

const EditBinModal = ({
  isModalOpen = false,
  setIsModalOpen,
  bin,
  onUpdated = () => {},
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    bin_location_no: bin?.bin_location_no ?? "",
    bin_priority: bin?.bin_priority ?? "",
    max_quantity: bin?.max_quantity ?? "",
    max_weight: bin?.max_weight ?? "",
    is_sellable_bin_location: bin?.is_sellable_bin_location ?? true,
    is_non_sellable_bin_location: bin?.is_non_sellable_bin_location ?? false,
    is_expired_bin_location: bin?.is_expired_bin_location ?? false,
  };

  const onSubmit = (values) => {
    setLoading(true);

    const newPriority =
      values.bin_priority === "" ? null : Number(values.bin_priority);

    const changes = {
      bin_location_no: values.bin_location_no,
      is_expired_bin_location: !!values.is_expired_bin_location,
      is_sellable_bin_location: !!values.is_sellable_bin_location,
      is_non_sellable_bin_location: !!values.is_non_sellable_bin_location,
      bin_priority: newPriority,
      max_quantity:
        values.max_quantity === "" ? null : Number(values.max_quantity),
      max_weight: values.max_weight === "" ? null : Number(values.max_weight),
    };

    // Swap semantics (if another bin in this warehouse already holds
    // bin_priority) are enforced server-side in updateSingleBinTx() — the
    // bin list is paginated now, so this modal can't reliably see every bin
    // to detect a conflict itself.
    dispatch(
      warehouseBinUpdateAction(
        { single: { bin_no_id: bin?.bin_no_id, changes } },
        () => {
          setLoading(false);
          successToast("Bin updated successfully");
          setIsModalOpen(false);
          onUpdated();
        },
        (data) => {
          setLoading(false);
          errorToast(data?.message);
        }
      )
    );
  };

  return (
    <Transition show={isModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => setIsModalOpen(false)}
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="mx-auto w-full max-w-2xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
              <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ handleSubmit }) => {
                  return (
                    <Form>
                      <div className="flex justify-between px-6 py-5">
                        <h2 className="fw_500">Edit Bin</h2>
                        <button
                          type="button"
                          className="close-button bg-transparent border-none p-0 hover:bg-787878"
                          onClick={() => setIsModalOpen(false)}
                        >
                          <CloseIcon />
                        </button>
                      </div>

                      <div className="px-6">
                        <InputField
                          type="text"
                          name="bin_location_no"
                          placeholder="Enter bin location no."
                          label="Bin Location No."
                          isRequired={true}
                          labelWidth="9rem"
                          allowSpecial={true}
                        />
                        <InputField
                          type="number"
                          name="bin_priority"
                          placeholder="Optional"
                          label="Priority"
                          isRequired={false}
                          labelWidth="9rem"
                        />
                        <p className="text-667085 fs_12 -mt-2 mb-3">
                          If another bin already has this priority, the two
                          bins will swap priorities.
                        </p>
                        <div className="flex gap-10">
                          <div className="flex-1">
                            <InputField
                              type="number"
                              name="max_quantity"
                              placeholder="Optional"
                              label="Max Quantity"
                              isRequired={false}
                              labelWidth="9rem"
                            />
                          </div>
                          <div className="flex-1">
                            <InputField
                              type="number"
                              name="max_weight"
                              placeholder="Optional"
                              label="Max Weight"
                              isRequired={false}
                              labelWidth="9rem"
                            />
                          </div>
                        </div>
                        <div className="mt-3">
                          <CheckBox items={checkboxItems} />
                        </div>
                      </div>

                      <div className="px-6 flex justify-between gap-4 py-5">
                        <DraftButton
                          onclkFn={() => setIsModalOpen(false)}
                          text="Cancel"
                        />
                        <AddButton
                          loading={loading}
                          onclkFn={handleSubmit}
                          type="submit"
                          className="bg-primary text-white px-8 py-2 rounded flex justify-center fw_500 text-sm"
                          text="Save Changes"
                          prefixIcon={false}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditBinModal;
